// Colores propios (C3D-17): los que el usuario elige fuera de la paleta de
// filamentos, con el selector libre o con el cuentagotas.
//
// Se recuerdan los últimos en el navegador para que aparezcan en todas las
// paletas como "Tus colores". Son una comodidad de quien diseña, no del diseño:
// el color de cada pieza ya se guarda con ella.
import { useEffect, useState } from 'react';
import { PALETTE, VACIADO_COLOR } from './colorPalette.js';

const STORAGE_KEY = 'customiz3r:customColors';
const CHANGE_EVENT = 'customiz3r:custom-colors';
const MAX_CUSTOM_COLORS = 8;
// El negro "de verdad" está reservado para el vaciado; el negro que se pinta
// es éste, igual que en la paleta.
const PRINTABLE_BLACK = '#000001';

/** `#RRGGBB` en minúsculas, o null si no es un color de 6 cifras. */
export const normalizeCustomColor = (value) => {
    const hex = String(value || '').trim().toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(hex)) return null;
    return hex === VACIADO_COLOR ? PRINTABLE_BLACK : hex;
};

const isPaletteColor = (hex) => PALETTE.some(entry => entry.value.toLowerCase() === hex);

export const getCustomColors = () => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');
        return Array.isArray(stored) ? stored.map(normalizeCustomColor).filter(Boolean) : [];
    } catch {
        return [];
    }
};

/** Pone `value` el primero de "Tus colores" (si no es ya de la paleta). */
export const rememberCustomColor = (value) => {
    const hex = normalizeCustomColor(value);
    if (!hex || isPaletteColor(hex)) return;
    const next = [hex, ...getCustomColors().filter(color => color !== hex)].slice(0, MAX_CUSTOM_COLORS);
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        // Sin almacenamiento (modo privado): el color se aplica igual.
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
};

/** Quita `value` de "Tus colores". Las piezas que ya lo usan no cambian. */
export const forgetCustomColor = (value) => {
    const hex = normalizeCustomColor(value);
    if (!hex) return;
    try {
        window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(getCustomColors().filter(color => color !== hex)),
        );
    } catch {
        return;
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
};

/** "Tus colores", al día en todas las paletas abiertas a la vez. */
export const useCustomColors = () => {
    const [colors, setColors] = useState(getCustomColors);
    useEffect(() => {
        const refresh = () => setColors(getCustomColors());
        window.addEventListener(CHANGE_EVENT, refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener(CHANGE_EVENT, refresh);
            window.removeEventListener('storage', refresh);
        };
    }, []);
    return colors;
};

export const hasScreenEyeDropper = () => typeof window !== 'undefined' && 'EyeDropper' in window;

/**
 * Cuentagotas del navegador (Chrome/Edge): un color de cualquier punto de la
 * pantalla. null si se cancela o el navegador no lo tiene.
 */
export const pickScreenColor = async () => {
    if (!hasScreenEyeDropper()) return null;
    try {
        const { sRGBHex } = await new window.EyeDropper().open();
        return normalizeCustomColor(sRGBHex);
    } catch {
        return null;
    }
};

const firstFill = (object) => {
    if (!object) return null;
    if (object._objects) {
        for (const child of object._objects) {
            const fill = firstFill(child);
            if (fill) return fill;
        }
        return null;
    }
    // Un vaciado se pinta con un patrón; su color real va aparte.
    const fill = typeof object._vaciadoFill === 'string' ? object._vaciadoFill : object.fill;
    return typeof fill === 'string' ? fill : null;
};

/**
 * Cuentagotas sobre el lienzo, para los navegadores sin el del sistema: el
 * siguiente clic en un objeto devuelve su color, sin cambiar la selección.
 * Escape o un clic en vacío lo cancelan (null).
 */
export const pickCanvasColor = (canvas) => new Promise((resolve) => {
    if (!canvas) {
        resolve(null);
        return;
    }
    const objects = canvas.getObjects();
    const selectable = objects.map(object => [object, object.selectable]);
    const previousCursor = canvas.defaultCursor;
    const previousHover = canvas.hoverCursor;
    const previousSelection = canvas.selection;
    // Que el clic encuentre el objeto pero no lo seleccione.
    objects.forEach(object => { object.selectable = false; });
    canvas.selection = false;
    canvas.defaultCursor = 'crosshair';
    canvas.hoverCursor = 'crosshair';

    const finish = (color) => {
        canvas.off('mouse:down', onDown);
        window.removeEventListener('keydown', onKey);
        selectable.forEach(([object, value]) => { object.selectable = value; });
        canvas.selection = previousSelection;
        canvas.defaultCursor = previousCursor;
        canvas.hoverCursor = previousHover;
        canvas.requestRenderAll();
        resolve(color);
    };
    const onDown = (event) => finish(normalizeCustomColor(firstFill(event.target)));
    const onKey = (event) => {
        if (event.key === 'Escape') finish(null);
    };
    canvas.on('mouse:down', onDown);
    window.addEventListener('keydown', onKey);
});

// Conversiones del selector propio (HSV: tono 0..360, saturación y brillo 0..1).
export const hexToHsv = (hex) => {
    const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!match) return null;
    const [r, g, b] = [0, 2, 4].map(i => parseInt(match[1].slice(i, i + 2), 16) / 255);
    const max = Math.max(r, g, b);
    const delta = max - Math.min(r, g, b);
    let h = 0;
    if (delta) {
        if (max === r) h = ((g - b) / delta) % 6;
        else if (max === g) h = (b - r) / delta + 2;
        else h = (r - g) / delta + 4;
    }
    return { h: ((h * 60) + 360) % 360, s: max ? delta / max : 0, v: max };
};

export const hsvToHex = ({ h, s, v }) => {
    const f = (n) => {
        const k = (n + h / 60) % 6;
        return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
    };
    return `#${[f(5), f(3), f(1)]
        .map(c => Math.round(c * 255).toString(16).padStart(2, '0'))
        .join('')}`;
};
